import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { SkeletonGrid } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { IconInbox, IconArrowRight } from '../components/ui/icons';
import { submissions } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { submissionStatusToChip, type ChipTone } from '../lib/tokens';
import { useToast } from '../components/ui/Toast';
import type { Submission, SubmissionBox, SubmissionStatus } from '../lib/types';

const CHIP_TEXT: Record<ChipTone, string> = {
  pending: 'text-secondary',
  processing: 'text-info',
  review: 'text-warn',
  complete: 'text-success',
  failed: 'text-danger',
  sold: 'text-success',
  neutral: 'text-muted',
};

function StatusBadge({ status }: { status: SubmissionStatus }) {
  const { t } = useI18n();
  const label: Record<SubmissionStatus, string> = {
    pending: t.submissions.statusPending,
    accepted: t.submissions.statusAccepted,
    declined: t.submissions.statusDeclined,
    withdrawn: t.submissions.statusWithdrawn,
  };
  const tone = submissionStatusToChip(status);
  return (
    <span className={`type-label inline-flex items-center rounded-sm border border-border-strong bg-surface-2 px-2 py-0.5 ${CHIP_TEXT[tone]}`}>
      {label[status]}
    </span>
  );
}

function SubmissionRow({
  sub,
  box,
  busy,
  onAction,
}: {
  sub: Submission;
  box: SubmissionBox;
  busy: boolean;
  onAction: (id: string, action: 'accept' | 'decline' | 'withdraw') => void;
}) {
  const { t, fmt } = useI18n();
  const isPending = sub.status === 'pending';

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="type-heading-2 text-primary truncate" title={sub.asset_title ?? sub.asset_id}>
            {sub.asset_title ?? sub.asset_id}
          </h3>
          <p className="type-caption text-muted mt-1 tnum">{fmt.date(sub.created_at)}</p>
        </div>
        <StatusBadge status={sub.status} />
      </div>

      {sub.message ? <p className="type-body-sm text-secondary">{sub.message}</p> : null}

      {isPending ? (
        <div className="flex flex-wrap gap-2">
          {box === 'inbox' ? (
            <>
              <Button variant="primary" size="sm" loading={busy} onClick={() => onAction(sub.id, 'accept')}>
                {t.submissions.accept}
              </Button>
              <Button variant="destructive" size="sm" loading={busy} onClick={() => onAction(sub.id, 'decline')}>
                {t.submissions.decline}
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" loading={busy} onClick={() => onAction(sub.id, 'withdraw')}>
              {t.submissions.withdraw}
            </Button>
          )}
        </div>
      ) : null}
    </Card>
  );
}

export function Submissions() {
  const { t } = useI18n();
  const toast = useToast();
  const [box, setBox] = useState<SubmissionBox>('inbox');
  const [rows, setRows] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(
    async (which: SubmissionBox) => {
      setLoading(true);
      try {
        const res = await submissions.list(which);
        setRows(res.submissions);
        setError(null);
      } catch (err) {
        setError(apiErrorMessage(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void load(box);
  }, [box, load]);

  const act = async (id: string, action: 'accept' | 'decline' | 'withdraw') => {
    setBusyId(id);
    try {
      await submissions[action](id);
      toast.push(t.submissions[action === 'accept' ? 'accepted' : action === 'decline' ? 'declined' : 'withdrawn'], 'success');
      await load(box);
    } catch (err) {
      toast.push(apiErrorMessage(err, t), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const tab = (which: SubmissionBox, label: string) => (
    <button
      type="button"
      onClick={() => setBox(which)}
      className={[
        'h-9 rounded-sm px-4 type-label transition-colors duration-fast ease-machine focus-ring',
        box === which ? 'bg-accent-tint text-accent border border-accent' : 'text-secondary hover:bg-surface-3 border border-transparent',
      ].join(' ')}
    >
      {label}
    </button>
  );

  return (
    <div>
      <PageHeader title={t.submissions.title} subtitle={t.submissions.subtitle} />

      <div className="mb-6 flex gap-2">
        {tab('inbox', t.submissions.inbox)}
        {tab('outbox', t.submissions.outbox)}
      </div>

      {error ? (
        <p className="type-body-sm text-danger mb-4" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <SkeletonGrid count={3} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<IconInbox size={48} />}
          title={t.submissions.title}
          body={box === 'inbox' ? t.submissions.emptyInbox : t.submissions.emptyOutbox}
          action={
            box === 'outbox' ? (
              <Link to="/vocal">
                <Button size="lg" iconRight={<IconArrowRight size={16} />}>
                  {t.vocal.title}
                </Button>
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
          {rows.map((sub) => (
            <SubmissionRow key={sub.id} sub={sub} box={box} busy={busyId === sub.id} onAction={act} />
          ))}
        </div>
      )}
    </div>
  );
}
